import React, { useState } from "react";
import { Scale, FileCheck2, Lock, Share2, ExternalLink } from "lucide-react";
import { api } from "@/lib/apiClient";
import { Justice } from "@/components/Justice";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

function Item({ value, icon: Icon, title, children, testid }) {
  return (
    <AccordionItem value={value} style={{ borderColor: "var(--ek-border)" }}>
      <AccordionTrigger data-testid={testid} className="py-3 hover:no-underline">
        <span className="flex items-center gap-2 text-left">
          <Icon className="h-4 w-4 shrink-0" style={{ color: "var(--ek-primary)" }} />
          <span className="ek-mono-label" style={{ color: "var(--ek-text)" }}>{title}</span>
        </span>
      </AccordionTrigger>
      <AccordionContent>
        <div className="space-y-2.5 text-[13px] leading-relaxed pb-1" style={{ color: "var(--ek-text-secondary)" }}>
          {children}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

function Term({ label, children }) {
  return (
    <p><b style={{ color: "var(--ek-text)" }}>{label}:</b> {children}</p>
  );
}

const LINKS = [
  { label: "Terms of Service", href: "/terms" },
  { label: "Privacy Policy", href: "/privacy" },
  { label: "AI & Evidence Policy", href: "/privacy" },
  { label: "Security", href: "/privacy" },
  { label: "Data & Account Controls", href: "/delete-account" },
];

export default function OnboardingDisclaimer({ onAcknowledge }) {
  const [checked, setChecked] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const ready = checked && typed.trim().toUpperCase() === "I UNDERSTAND";

  const ack = async () => {
    if (!ready) return;
    setBusy(true);
    try { await api.post("/account/ack-onboarding", {}); } catch (e) { console.error("ack failed", e); }
    try { localStorage.setItem("ek_onboarded", "1"); } catch { /* ignore */ }
    onAcknowledge();
  };

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto ek-scanline" style={{ background: "rgba(4,8,16,0.94)", backdropFilter: "blur(6px)" }} data-testid="onboarding-disclaimer">
      <div className="min-h-full flex items-start sm:items-center justify-center p-4">
        <div className="max-w-xl w-full rounded-xl border p-5 sm:p-6 my-6" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}>
          <div className="flex items-center gap-3 mb-4">
            <Justice size={52} />
            <div>
              <h1 className="font-head font-bold text-2xl tracking-tight leading-tight" data-testid="onboarding-title">Welcome to EvidenceKeeper</h1>
              <p className="ek-mono-label mt-1" style={{ color: "var(--ek-warning)" }} data-testid="onboarding-subtitle">LEGAL NATURE AND BOUNDARY ACKNOWLEDGMENT</p>
            </div>
          </div>

          <p className="text-[13px] leading-relaxed mb-3" style={{ color: "var(--ek-text-secondary)" }} data-testid="onboarding-positioning">
            EvidenceKeeper is an evidence-preservation and organization platform. It can categorize records using
            user-selected case types, court-order provisions, and jurisdictional organizational frameworks, but it does not
            provide legal advice, recommend legal strategy, or determine legal outcomes.
          </p>
          <p className="text-[13px] leading-relaxed mb-3" style={{ color: "var(--ek-text-muted)" }}>
            Please review each section below before you begin. Tap a section to expand it.
          </p>

          <Accordion type="single" collapsible defaultValue="legal" className="w-full">
            <Item value="legal" icon={Scale} title="1 · LEGAL INFORMATION" testid="onboarding-sec-legal">
              <Term label="No Attorney-Client Relationship">Use of EvidenceKeeper does not create an attorney-client relationship. EvidenceKeeper is not a law firm and does not provide legal representation.</Term>
              <Term label="Not Legal Advice">EvidenceKeeper provides tools for preserving, organizing, reviewing, and presenting user-provided records. EvidenceKeeper and Justice™ do not provide legal advice or determine legal rights, strategy, outcomes, or the legal significance of evidence.</Term>
              <Term label="Evidentiary Use">EvidenceKeeper does not determine or guarantee the admissibility, authenticity, evidentiary weight, or legal significance of any record. Those determinations are made by the applicable court or other authorized decision-maker.</Term>
              <Term label="User Review">Users are responsible for reviewing AI-extracted information, summaries, report selections, and other derived content for accuracy before relying upon or sharing it.</Term>
            </Item>

            <Item value="evidence" icon={FileCheck2} title="2 · EVIDENCE & AI" testid="onboarding-sec-evidence">
              <Term label="Original Evidence Preservation">EvidenceKeeper preserves the originally uploaded file separately from AI-generated summaries and other derived records. A SHA-256 cryptographic hash is generated and stored to support later file-integrity comparison.</Term>
              <Term label="AI-Assisted Content">Fact Filter™ and Justice™ may generate summaries, extracted facts, classifications, and other derived information from user-provided records. AI-generated content is maintained separately from the original source evidence and should be reviewed for accuracy before use.</Term>
              <Term label="Original Source Requirement">AI-generated summaries and extracted information are not replacements for the underlying source evidence. EvidenceKeeper maintains source links so derived information can be reviewed against the corresponding original record.</Term>
            </Item>

            <Item value="privacy" icon={Lock} title="3 · PRIVACY & SECURITY" testid="onboarding-sec-privacy">
              <Term label="Data Security">EvidenceKeeper uses encryption and access controls designed to protect stored and transmitted information. Specific security protections depend upon the type of data and processing being performed.</Term>
            </Item>

            <Item value="sharing" icon={Share2} title="4 · SHARING & LEGAL REQUESTS" testid="onboarding-sec-sharing">
              <Term label="Legal Requests">Information maintained by EvidenceKeeper may be subject to valid legal process. EvidenceKeeper handles requests for user information according to applicable law, its Privacy Policy, and the technical capabilities of its systems.</Term>
            </Item>
          </Accordion>

          <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-4" data-testid="onboarding-links">
            {LINKS.map((l) => (
              <a key={l.label} href={l.href} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-[12px] hover:underline"
                style={{ color: "var(--ek-primary)" }}>
                {l.label}<ExternalLink className="h-3 w-3" />
              </a>
            ))}
          </div>

          <label className="flex items-start gap-3 mt-5 cursor-pointer">
            <Checkbox checked={checked} onCheckedChange={(v) => setChecked(!!v)} data-testid="onboarding-checkbox" className="mt-0.5" />
            <span className="text-[13px] leading-relaxed" style={{ color: "var(--ek-text-secondary)" }}>
              I have reviewed the sections above and acknowledge that EvidenceKeeper is a documentation and organization tool that does not provide legal advice, legal strategy, or outcomes, and that I am responsible for reviewing AI-derived content for accuracy.
            </span>
          </label>

          <div className="mt-3">
            <label className="ek-mono-label block mb-1">TYPE EXACTLY <b style={{ color: "var(--ek-text)" }}>I UNDERSTAND</b> TO UNLOCK YOUR VAULT</label>
            <Input value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="onboarding-type-input"
              placeholder="I UNDERSTAND" className="rounded-md" style={{ background: "var(--ek-page)", borderColor: "var(--ek-border)", color: "var(--ek-text)" }} />
          </div>

          <Button onClick={ack} disabled={!ready || busy} data-testid="onboarding-ack-btn" className="w-full rounded-md mt-4 font-medium" style={{ background: ready ? "var(--ek-primary)" : "var(--ek-border)", color: "#fff", opacity: ready ? 1 : 0.6 }}>
            {busy ? "Initializing…" : "Initialize My Vault"}
          </Button>

          <p className="text-[10px] leading-snug mt-4 text-center" style={{ color: "var(--ek-text-muted)" }} data-testid="onboarding-footer">
            Reports are compiled, chronologized, and formatted from content you provide. EvidenceKeeper functions as an administrative documentation and layout utility and contains no legal opinions, strategy, predictions, or advice. Original files are preserved separately and fingerprinted with a SHA-256 integrity hash.
          </p>
        </div>
      </div>
    </div>
  );
}
